<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import {Link, usePage, router} from "@inertiajs/vue3";
import Button from "primevue/button";
import CreditsCounter from "../CreditsCounter.vue";
import Popover from "primevue/popover";
import AppSidebarLogo from "./AppSidebarLogo.vue";

const page = usePage();

const isNavigating = ref(false);
let navigatingTimer = null;

router.on('start', () => {
    navigatingTimer = setTimeout(() => { isNavigating.value = true; }, 200);
});
router.on('finish', () => {
    clearTimeout(navigatingTimer);
    isNavigating.value = false;
});

const items = computed(() => {
    return page.props.menu;
});

const op = ref();

const needsUpgrade = ref(false);
const getClassOfButton = (selected) => {
    const color = needsUpgrade.value ? 'yellow' : 'blue';
    if (selected) {
        return `[&_i]:bg-${color}-50 text-${color}-500 [&_i]:hover:bg-${color}-100 [&_i]:dark:hover:bg-${color}-900 [&_i]:dark:bg-${color}-950 `;
    }
    return 'text-neutral-600 [&_i]:hover:bg-neutral-100 [&_i]:dark:hover:bg-gray-700 hover:text-black dark:text-neutral-200 dark:hover:text-white';
}

const visibleCount = ref(items.value.length);

const onResize = () => {
    const availableHeight = window.innerHeight - 250;
    const itemHeight = 110;
    const maxVisible = Math.floor(availableHeight / itemHeight);
    if (maxVisible >= items.value.length - 1) {
        visibleCount.value = items.value.length;
    } else {
        visibleCount.value = Math.min(items.value.length, maxVisible);
    }
};

onMounted(() => {
    onResize();
    window.addEventListener('resize', onResize);

    // Initialize indicatorTop based on selected item
    updateIndicator();
});

onUnmounted(() => {
    window.removeEventListener('resize', onResize);
});

const sortedItems = computed(() => {
    const _items = items.value.filter(el => !el.onlyOnMobile)
    // If visibleCount is greater or equal to items length, just return items
    if (visibleCount.value >= _items.length) {
        return _items;
    }
    // Find the selected item index
    const selectedIndex = _items.findIndex(item => item.selected);
    if (selectedIndex === -1) {
        // No selected item, just return sliced items
        return _items;
    }
    // If selected item is already visible, return items as is
    if (selectedIndex < visibleCount.value) {
        return _items;
    }
    // Otherwise, move selected item to the end of visible items
    // and move the last visible item to hidden items
    const newItems = [..._items];
    const selectedItem = newItems.splice(selectedIndex, 1)[0];
    // Insert selected item at visibleCount - 1 index
    newItems.splice(visibleCount.value - 1, 0, selectedItem);
    return newItems;
});

const hiddenItems = computed(() => sortedItems.value.slice(visibleCount.value));

const indicatorTop = ref(0);
const itemHeight = 90;
const offset = 0;
const selectItem = (item) => {
    // Přepočítej celý seznam podle visibleCount
    const selectedIndex = items.value.findIndex(el => el.selected);
    if (selectedIndex >= visibleCount.value) {
        // Odstraň vybranou položku a vlož ji na konec viditelné části
        items.value.splice(selectedIndex, 1);
        items.value.splice(visibleCount.value - 1, 0, item);
    }

    updateIndicator();
};
const updateIndicator = () => {
    const selectedIndex = sortedItems.value.findIndex(item => item.selected);
    needsUpgrade.value = sortedItems.value.find(item => item.selected)?.needsUpgrade;
    if (selectedIndex !== -1) {
        indicatorTop.value = selectedIndex * itemHeight + offset;
    } else {
        indicatorTop.value = -800;
    }
};

watch(() => items.value, () => {
    updateIndicator();
}, { deep: true });

watch(() => sortedItems.value, () => {
    updateIndicator();
});

</script>

<template>
    <div class="flex flex-col pt-12 gap-2 items-center  h-full">
        <Link href="/"><AppSidebarLogo :badge-class="$page.props.logo" :is-navigating="isNavigating" /></Link>
        <div class="relative w-full">
            <div class="absolute left-0 w-1 rounded-e-lg transition-all duration-300" :class="{ 'bg-blue-500': !needsUpgrade, 'bg-yellow-500': needsUpgrade }" :style="{ top: indicatorTop + 20 + 'px', height: itemHeight - 40 + 'px' }"></div>
            <Link v-for="(item, index) in sortedItems.slice(0, visibleCount)" :href="item.route" @click="selectItem(item)" :key="item.label" prefetch class="justify-center relative cursor-pointer items-center flex  w-full" style="height: 90px;">
                <div :class="getClassOfButton(item.selected)" class="p-2 flex flex-col items-center">
                    <i :class="item.icon" class="!text-2xl w-12 h-12 flex justify-center items-center rounded-lg"></i>
                    <div class="text-sm mt-1">{{item.label}}</div>
                </div>
                <div v-if="item.needsUpgrade" class="absolute top-2 right-6 text-black bg-yellow-500 rounded-full w-5 flex justify-center items-center h-5 p-1">
                    <i class="pi pi-crown !text-xs"></i>
                </div>
            </Link>
        </div>

        <template v-if="hiddenItems.length > 0">
            <div class="min-w-12 rounded-lg mt-4 min-h-12 flex justify-center items-center cursor-pointer text-neutral-600 dark:text-neutral-200 hover:bg-neutral-100 hover:text-black !text-xl pi pi-bars" @click="(target) => op.toggle(target)" />
            <Popover ref="op">
                <div class="flex flex-col gap-1 p-1">
                    <Link v-for="item in hiddenItems" @click="selectItem(item)" :href="item.route" prefetch class="justify-start hover:bg-neutral-50 cursor-pointer items-center flex w-full px-2 my-1 py-1">
                        <i :class="item.icon" class="!text-xl w-6 h-6 flex justify-center items-center rounded-lg mr-3"></i>
                        <div class="text-sm">{{item.label}}</div>
                        <div v-if="item.needsUpgrade" class="ml-4 text-black bg-yellow-500 rounded-full w-5 flex justify-center items-center h-5 p-1">
                            <i class="pi pi-crown !text-xs"></i>
                        </div>
                    </Link>
                </div>
            </Popover>
        </template>

        <div class="grow"></div>
        <credits-counter class="m-5"></credits-counter>
    </div>
</template>

<style scoped lang="scss">

</style>
